export interface Category {
  id: string;
  name: string;
  icon: 'user' | 'smartphone' | 'home' | 'star' | 'smile' | 'clock' | 'activity' | 'gift' | 'grid' | 'shopping-bag';
  lucideIcon?: 'venus' | 'mars' | 'layout-grid' | 'baby';
  subcategories: Subcategory[];
}

export interface Subcategory {
  id: string;
  name: string;
  items?: string[];
}

export const CATEGORIES: Category[] = [
  {
    id: 'womens-fashion',
    name: "Women's Fashion",
    icon: 'shopping-bag', // fallback
    lucideIcon: 'venus',
    subcategories: [
      {
        id: 'womens-clothing',
        name: 'Clothing',
        items: [
          'T-Shirts & Tops',
          'Blouses',
          'Dresses',
          'Sweaters',
          'Jackets & Coats',
          'Pants & Leggings',
          'Skirts',
          'Shorts',
          'Activewear',
          'Sleepwear & Loungewear',
          'Swimwear'
        ]
      },
      {
        id: 'womens-shoes',
        name: 'Shoes',
        items: [
          'Sneakers',
          'Boots',
          'Heels',
          'Flats',
          'Sandals & Flip-Flops',
          'Loafers',
          'Slippers'
        ]
      },
      {
        id: 'womens-accessories',
        name: 'Accessories',
        items: [
          'Jewelry (Necklaces, Earrings, Bracelets)',
          'Hats & Caps',
          'Sunglasses',
          'Scarves',
          'Belts',
          'Gloves'
        ]
      },
      {
        id: 'womens-handbags',
        name: 'Handbags',
        items: [
          'Totes',
          'Crossbody Bags',
          'Shoulder Bags',
          'Clutches',
          'Backpacks',
          'Wallets'
        ]
      }
    ]
  },
  {
    id: 'mens-fashion',
    name: "Men's Fashion",
    icon: 'user', // fallback
    lucideIcon: 'mars',
    subcategories: [
      {
        id: 'mens-clothing',
        name: 'Clothing',
        items: [
          'T-Shirts & Tank Tops',
          'Button-Down Shirts',
          'Polo Shirts',
          'Hoodies & Sweatshirts',
          'Jackets & Coats',
          'Jeans',
          'Pants & Chinos',
          'Shorts',
          'Activewear',
          'Sleepwear & Loungewear',
          'Swimwear'
        ]
      },
      {
        id: 'mens-shoes',
        name: 'Shoes',
        items: [
          'Sneakers',
          'Boots',
          'Dress Shoes',
          'Loafers',
          'Sandals & Flip-Flops',
          'Slippers'
        ]
      },
      {
        id: 'mens-accessories',
        name: 'Accessories',
        items: [
          'Watches',
          'Hats & Beanies',
          'Sunglasses',
          'Belts',
          'Ties & Bowties',
          'Gloves'
        ]
      },
      {
        id: 'mens-bags',
        name: 'Bags',
        items: [
          'Backpacks',
          'Messenger Bags',
          'Briefcases',
          'Wallets'
        ]
      }
    ]
  },
  {
    id: 'kids',
    name: 'Kids',
    icon: 'smile', // fallback
    lucideIcon: 'baby',
    subcategories: [
      {
        id: 'kids-clothing',
        name: 'Clothing',
        items: [
          'T-Shirts & Tops',
          'Dresses',
          'Hoodies & Sweatshirts',
          'Jackets & Coats',
          'Pants & Leggings',
          'Shorts',
          'Activewear',
          'Sleepwear & Loungewear',
          'Swimwear'
        ]
      },
      {
        id: 'kids-shoes',
        name: 'Shoes',
        items: [
          'Sneakers',
          'Boots',
          'Sandals & Flip-Flops',
          'Dress Shoes',
          'Slippers'
        ]
      },
      {
        id: 'kids-accessories',
        name: 'Accessories',
        items: [
          'Hats & Caps',
          'Backpacks',
          'Sunglasses'
        ]
      }
    ]
  },
  {
    id: 'other',
    name: 'Other Categories (Coming Soon)',
    icon: 'grid', // fallback
    lucideIcon: 'layout-grid',
    subcategories: [
      {
        id: 'toys',
        name: 'Toys',
        items: [
          'Stuffed Animals',
          'Action Figures',
          'Dolls & Dollhouses',
          'Board Games & Puzzles',
          'Building Blocks & Construction Sets',
          'Outdoor & Sports Toys'
        ]
      },
      {
        id: 'beauty-grooming',
        name: 'Beauty & Grooming',
        items: [
          'Skincare',
          'Makeup',
          'Hair Care',
          'Fragrances',
          'Tools & Accessories'
        ]
      },
      {
        id: 'electronics',
        name: 'Electronics',
        items: [
          'Cell Phones & Accessories',
          'Computers & Tablets',
          'Cameras & Photography',
          'Video Games & Consoles',
          'Wearable Technology'
        ]
      },
      {
        id: 'home',
        name: 'Home',
        items: [
          'Furniture',
          'Home Decor',
          'Bedding',
          'Kitchen & Dining',
          'Storage & Organization'
        ]
      },
      {
        id: 'vintage',
        name: 'Vintage',
        items: [
          'Clothing',
          'Collectibles',
          'Home Decor',
          'Jewelry',
          'Electronics'
        ]
      },
      {
        id: 'sports',
        name: 'Sports',
        items: [
          'Athletic Apparel',
          'Outdoor Equipment',
          'Fitness Accessories',
          'Fan Shop',
          'Sports Memorabilia'
        ]
      },
      {
        id: 'handmade',
        name: 'Handmade',
        items: [
          'Jewelry',
          'Home Decor',
          'Accessories',
          'Art',
          'Toys'
        ]
      },
      {
        id: 'office-supplies',
        name: 'Office Supplies',
        items: [
          'Stationery',
          'Office Electronics',
          'Storage & Organization',
          'Desk Accessories'
        ]
      },
      {
        id: 'pet-supplies',
        name: 'Pet Supplies',
        items: [
          'Pet Apparel',
          'Grooming & Health',
          'Toys',
          'Beds & Furniture'
        ]
      },
      {
        id: 'automotive',
        name: 'Automotive',
        items: [
          'Car Accessories',
          'Tools & Equipment',
          'Replacement Parts'
        ]
      },
      {
        id: 'health-wellness',
        name: 'Health & Wellness',
        items: [
          'Fitness Equipment',
          'Nutrition & Supplements',
          'Wellness Accessories'
        ]
      },
      {
        id: 'musical-instruments',
        name: 'Musical Instruments',
        items: [
          'Guitars & Basses',
          'Keyboards & Pianos',
          'Drums & Percussion',
          'Wind & String Instruments'
        ]
      }
    ]
  }
]; 